import { Colors, Lightning } from '@lightningjs/sdk';
import { constants } from 'aliases';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { formatVideoTime } from 'support/contentUtils';
import { getFontFaceFromStyle } from 'support/textUtils';
import PlaybackProgressBar, {
  FASTFORWARD_ICON,
  PROGRESS_BAR_HEIGHT,
  PROGRESS_BAR_WIDTH,
  REWIND_ICON,
} from 'components/common/progressBar/PlaybackProgressBar';
import { translate } from 'support/translate';
import TextLabel from 'components/common/TextLabel';

const FOCUSED_ALPHA = 1;
const UNFOCUSED_ALPHA = 0.6;

const PROGRESS_BAR_CONTAINER_H = 101;
const PROGRESS_BAR_HEIGHT_WITH_MARGIN = 52;
const PROGRESS_BAR_MARGIN = 16;
const PROGRESS_TIME_H = 49;

const FONT_SIZE = 29;
const MARGIN_SMALL = 8;
const MARGIN_LARGE = 16;

const SEEK_BALL_SIZE = 40;
const LIVE_EDGE_WIDTH = 12;

export interface LiveProgressBarTemplateSpec
  extends Lightning.Component.TemplateSpec {
  ProgressInfo: {
    ProgressTime: typeof Lightning.Texture;
    SeekIcon: Lightning.textures.ImageTexture;
    SeekSpeed: typeof TextLabel;
  };
  ProgressBar: {
    PlaybackProgressBar: typeof PlaybackProgressBar;
    SeekBall: Lightning.textures.RectangleTexture;
    SeekBallShadow: Lightning.textures.RectangleTexture;
    LiveEdge: Lightning.textures.RectangleTexture;
  };
}

export default class LiveProgressBar
  extends HoverableComponent<LiveProgressBarTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<LiveProgressBarTemplateSpec>
{
  private _ProgressInfo = this.getByRef('ProgressInfo')!;
  private _ProgressTime = this._ProgressInfo.getByRef('ProgressTime')!;
  private _SeekIcon = this._ProgressInfo.getByRef('SeekIcon')!;
  private _SeekSpeed = this._ProgressInfo.getByRef('SeekSpeed')!;
  private _ProgressBar = this.getByRef('ProgressBar')!;
  private _PlaybackProgressBar = this._ProgressBar.getByRef(
    'PlaybackProgressBar',
  )!;
  private _SeekBall = this._ProgressBar.getByRef('SeekBall')!;
  private _LiveEdge = this._ProgressBar.getByRef('LiveEdge')!;

  private _dvrWindowSize = 0;

  get playbackProgressBar() {
    return this._PlaybackProgressBar;
  }

  set dvrWindowSize(totalSeconds: number) {
    this._dvrWindowSize = totalSeconds;
  }

  set progressTime(secondsBehindLiveEdge: number) {
    this._ProgressTime.patch({
      text: {
        text: `-${formatVideoTime(secondsBehindLiveEdge)}`,
      },
    });

    this.updateSeekBallPosition(secondsBehindLiveEdge);
  }

  set seekSpeed(speed: number) {
    this.updateSeekIcon(speed);
  }

  set hasDVR(hasDVR: boolean) {
    this._SeekBall.visible = hasDVR;
    this._LiveEdge.visible = hasDVR;
  }

  static override _template(): Lightning.Component.Template<Lightning.Component.TemplateSpecLoose> {
    return {
      w: PROGRESS_BAR_WIDTH,
      h: PROGRESS_BAR_CONTAINER_H,
      flex: {
        direction: 'column',
      },
      ProgressInfo: {
        flex: {
          direction: 'row',
        },
        h: PROGRESS_TIME_H,
        ProgressTime: {
          flexItem: {
            marginRight: MARGIN_LARGE,
          },
          text: {
            fontSize: FONT_SIZE,
            fontFace: getFontFaceFromStyle('bold'),
            textColor: Colors('text').get(),
          },
        },
        SeekIcon: {
          visible: false,
          flexItem: {
            marginRight: MARGIN_SMALL,
          },
        },
        SeekSpeed: {
          type: TextLabel,
          visible: false,
          text: {
            fontSize: FONT_SIZE,
            fontFace: getFontFaceFromStyle('bold'),
            textColor: Colors('text').get(),
          },
        },
      },
      ProgressBar: {
        w: PROGRESS_BAR_WIDTH,
        h: PROGRESS_BAR_HEIGHT_WITH_MARGIN,
        y: PROGRESS_BAR_MARGIN,
        PlaybackProgressBar: {
          type: PlaybackProgressBar,
          w: PROGRESS_BAR_WIDTH,
          h: PROGRESS_BAR_HEIGHT,
          alpha: UNFOCUSED_ALPHA,
          progressColor: Colors('appAccent').get(),
          backgroundColor: Colors('liveProgressBarBackground').get(),
          value: 1,
          zIndex: constants.ui.zIndex.foregroundLayer,
          progressRadius: 0,
          passSignals: {
            $onCursorClick: true,
            $onHover: true,
          },
        },
        SeekBall: {
          alpha: 1,
          y: (SEEK_BALL_SIZE - PROGRESS_BAR_HEIGHT) / 2,
          x: PROGRESS_BAR_WIDTH,
          mountX: 0.5,
          mountY: 0.5,
          rect: true,
          color: Colors('appAccent').get(),
          w: SEEK_BALL_SIZE,
          h: SEEK_BALL_SIZE,
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            radius: SEEK_BALL_SIZE / 2,
          },
          zIndex: constants.ui.zIndex.foregroundLayer + 2,
        },
        LiveEdge: {
          rect: true,
          zIndex: constants.ui.zIndex.foregroundLayer + 1,
          w: LIVE_EDGE_WIDTH,
          h: PROGRESS_BAR_HEIGHT,
          x: PROGRESS_BAR_WIDTH - LIVE_EDGE_WIDTH,
        },
      },
    };
  }

  override _getFocused() {
    return this._PlaybackProgressBar;
  }

  override _handleClick(target: unknown) {
    if (target !== this._PlaybackProgressBar) {
      return true;
    }
  }

  override _focus() {
    this.showSeekBall(true);
    this.setSmooth('alpha', FOCUSED_ALPHA);
  }

  override _unfocus() {
    this.showSeekBall(false);
    this.setSmooth('alpha', UNFOCUSED_ALPHA);
  }

  showProgressTime(show: boolean) {
    this._ProgressTime.patch({ alpha: show ? 1 : 0 });
  }

  private showSeekBall(show: boolean) {
    this._SeekBall.visible = show;
  }

  private updateSeekBallPosition(secondsBehindLiveEdge: number) {
    if (this._dvrWindowSize <= 0) return;

    const positionRatio = 1 - secondsBehindLiveEdge / this._dvrWindowSize;
    this._PlaybackProgressBar.value = positionRatio;
    this._SeekBall.x = positionRatio * this._PlaybackProgressBar.w;
  }

  private updateSeekIcon(speed: number) {
    this._SeekIcon.visible = !!speed;
    this._SeekSpeed.visible = !!speed;
    if (speed) this._ProgressTime.alpha = 1;

    this._SeekIcon.patch({
      ...(speed > 0 ? FASTFORWARD_ICON : REWIND_ICON),
    });

    this._SeekSpeed.patch({
      text: {
        text: translate('playback.seekSpeed', Math.abs(speed)),
      },
    });
  }
}
